import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag, Select } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import tvShowService from "../../../services/tvShowService";
import masterService from "../../../services/masterService";
import videoService from "../../../services/videoService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const TvShowList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();

  // State management
  const [loading, setLoading] = useState(false);
  const [tvShows, setTvShows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} TV shows`,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
  });

  // What is typed in the box, kept separate from filters.search so the input stays
  // responsive while the term it actually searches on settles. Typing used to cost
  // two requests per keystroke: handleSearchChange called getTvShows directly and
  // also set filters.search, which getTvShows depends on, so the effect below
  // fired a second one.
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const [genres, setGenres] = useState([]);
  const [ratings, setRatings] = useState([]);

  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);

  const fetchGenres = async () => {
    const response = await masterService.getGenres({ isAll: true });
    const data = response.data.data;
    const list = data?.map((item) => ({
      value: item.genre_id,
      label: item.genre_name,
    }));
    console.log("list===>genres", list);
    setGenres(list);
  };

  const fetchRatings = async () => {
    const response = await videoService.getAllVideoRatings({ isAll: true });
    const data = response.data.data;
    const list = data?.map((item) => ({
      value: item.vr_id,
      label: item.rating_name,
    }));
    console.log("list===>ratings", list);
    setRatings(list);
  };

  useEffect(() => {
    fetchGenres();
    fetchRatings();
    setPageTitle("Series");
  }, []);

  const getTvShows = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.search,
          genre_list: selectedGenres,
          rating_list: selectedRatings,
          ...params,
        };

        console.log("Fetching TV shows with params:", requestParams);
        const response = await tvShowService.getAllTvShow(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("TV Shows API Response:", response);

        if (response.data && response.data.status) {
          const tvShowsData = response.data.data?.data || [];
          const paginationData = response.data.data?.pagination || {};

          console.log("TV shows data:", tvShowsData);
          console.log("Pagination data:", paginationData);

          // Ensure tvShowsData is an array
          if (Array.isArray(tvShowsData)) {
            setTvShows(tvShowsData);
            setPagination((prev) => ({
              ...prev,
              total: paginationData.total || tvShowsData.length,
              current: paginationData.currentPage || prev.current,
            }));
          } else {
            console.error("TV shows data is not an array:", tvShowsData);
            setTvShows([]);
          }
        } else {
          notify.error({ message: "Failed to fetch TV shows" });
          setTvShows([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching TV shows:", error);
        notify.error({ message: "Failed to fetch TV shows" });
        setTvShows([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [
      filters.search,
      pagination.pageSize,
      pagination.current,
      selectedGenres,
      selectedRatings,
    ]
  );

  // Load TV shows data
  useEffect(() => {
    getTvShows();
  }, [getTvShows]);

  // Handle search

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      // Only the state is set — the effect that watches filters.search issues the
      // request, so there is exactly one per settled term rather than two.
      const term = value.trim();
      setFilters((prev) => (prev.search === term ? prev : { ...prev, search: term }));
      setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const term = searchInput.trim();
    setFilters((prev) => (prev.search === term ? prev : { ...prev, search: term }));
    setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getTvShows({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search,
      genre_list: selectedGenres,
      rating_list: selectedRatings,
    });
  };

  // Handle add TV show
  const handleAddTvShow = () => {
    navigate("/series/add/new");
  };

  // Handle edit TV show
  const handleEditTvShow = (tvShowId) => {
    navigate(`/series/edit/${tvShowId}`);
  };

  // Table columns
  const columns = [
    {
      title: "TV Show Name",
      dataIndex: "tv_show_name",
      key: "tv_show_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.tv_show_name.localeCompare(b.tv_show_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "No of Seasons",
      dataIndex: "no_of_season",
      key: "no_of_season",
      width: 150,
      defaultSortOrder: "ascend",
      sorter: (a, b) => (a.no_of_season || 0) - (b.no_of_season || 0),
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || 0}</Text>,
    },
    {
      title: "Status",
      dataIndex: "tv_show_is_active",
      key: "tv_show_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditTvShow(record.tv_show_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
    <Card>
      <div className="flex items-center justify-between gap-4">
        <Title level={4} style={{ margin: 0 }}>
          TV Shows ({pagination.total})
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddTvShow}
          size="middle"
        >
          Add Series
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 my-4">
      <Search
          placeholder="Search TV shows..."
          allowClear
          value={searchInput}
          onChange={handleSearchChange}
          onSearch={handleSearchNow}
          enterButton={<SearchOutlined />}
        />
        <Select
          options={genres}
          placeholder="Select genre"
          allowClear
          showSearch
          optionFilterProp="label"
          mode="multiple"
          value={selectedGenres}
          onChange={setSelectedGenres}
        />
        <Select
          options={ratings}
          placeholder="Select rating"
          allowClear
          showSearch
          optionFilterProp="label"
          mode="multiple"
          value={selectedRatings}
          onChange={setSelectedRatings}
        />

      </div>

      <Table
        columns={columns}
        dataSource={tvShows}
        rowKey="tv_show_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        size="middle"
        locale={{
          emptyText: "No TV shows found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default TvShowList;
